// src/pages/Dashboard.js
import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Container, Grid, Paper, CircularProgress, Alert,
    Button, Stack, Menu, MenuItem, ListItemIcon, ListItemText, TextField, 
    InputAdornment, Chip, IconButton, Tooltip, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Autocomplete, createFilterOptions, 
    Link, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { red } from '@mui/material/colors';
import { parseISO, format, differenceInHours } from 'date-fns';

// Icons
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import HealingIcon from '@mui/icons-material/Healing';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; 
import LinkIcon from '@mui/icons-material/Link'; 
import DeleteIcon from '@mui/icons-material/Delete';
import ProgramInviteGeneratorModal from '../components/ProgramInviteGeneratorModal'; 

// Auth and utils
import authFetch from '../utils/authFetch';
import { useAuth } from '../contexts/AuthContext';
import useDebounce from '../hooks/useDebounce';

// Modals & Sub-components
import InviteStaffModal from '../components/InviteStaffModal';
import BulkInviteModal from '../components/BulkInviteModal';
import ScheduleSurgeryModal from '../components/ScheduleSurgeryModal';
import UnifiedCareWizard from '../components/UnifiedCareWizard';

// --- API Functions ---
const fetchDashboardStats = () => authFetch(`/dashboard/stats`);
const searchPatientsAPI = async (searchQuery) => {
    const params = new URLSearchParams({ role: 'patient', limit: '50', offset: '0' });
    if (searchQuery && searchQuery.length >= 2) {
        params.append('search', searchQuery);
    }
    return authFetch(`/admin/users/?${params.toString()}`);
};
// ADDED: Functions to fetch all data for the dashboard list
const fetchAllEpisodes = (tenantId) => authFetch(`/episodes/?tenant_id=${tenantId}&limit=1000`);
const fetchAllAssignments = (tenantId) => authFetch(`/episodes/all_assignments/?tenant_id=${tenantId}&status=submitted`);


const KpiPanel = ({ children }) => (<Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.paper', borderRadius: 1.5, border: (theme) => `1px solid ${theme.palette.divider}`, flexGrow: 1 }}><Grid container spacing={2} textAlign="center" justifyContent="space-around">{children}</Grid></Paper>);
const KpiItem = ({ label, value, isLoading }) => (<Grid item xs={4}><Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase' }}>{label}</Typography>{isLoading ? <CircularProgress size={24} /> : <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{value ?? '0'}</Typography>}</Grid>);

const PatientRow = ({ patient, onScheduleSurgery, episodes, assignments, isLoadingDetails, onDelete, currentUserRole }) => {
    const { journeyChips, reviewCounts } = useMemo(() => {
        if (isLoadingDetails) {
            return { journeyChips: <CircularProgress size={20} />, reviewCounts: <CircularProgress size={20} /> };
        }

        const activeTherapeuticEpisodes = episodes
            .filter(ep => ep.program?.key !== 'lifetime_perpetual' && ep.status === 'active')
            .sort((a, b) => (a.surgery_datetime ? new Date(a.surgery_datetime) : new Date(b.start_date)) - (b.surgery_datetime ? new Date(b.surgery_datetime) : new Date(a.start_date)));

        if (activeTherapeuticEpisodes.length === 0) {
            const noPlanChip = <Chip icon={<InfoOutlinedIcon />} label="No Active Plan" size="small" variant="outlined" />;
            return { journeyChips: noPlanChip, reviewCounts: null };
        }

        const chips = [];
        const counts = [];

        activeTherapeuticEpisodes.forEach(episode => {
            const procedureName = episode.program?.name || episode.description || 'Care Plan';
            if (episode.surgery_datetime) {
                const surgeryDate = parseISO(episode.surgery_datetime);
                const hoursUntil = differenceInHours(surgeryDate, new Date());
                const isUrgent = hoursUntil >= 0 && hoursUntil <= 24;
                chips.push(
                    <Chip key={`${episode.id}-status`} icon={<EventAvailableIcon />} label={`${procedureName} on ${format(surgeryDate, 'MMM d @ h:mm a')}`}
                        size="small" variant={isUrgent ? 'filled' : 'outlined'} color={isUrgent ? 'error' : 'primary'}
                        sx={isUrgent ? { bgcolor: red[300], color: 'white' } : {}}
                        onClick={() => onScheduleSurgery(patient, [episode])} />
                );
            } else {
                chips.push(
                    <Chip key={`${episode.id}-status`} icon={<HourglassTopIcon />} label={`Awaiting Scheduling: ${procedureName}`}
                        size="small" color="warning" variant="outlined"
                        onClick={() => onScheduleSurgery(patient, [episode])} />
                );
            }

            const itemTypesToReview = ['questionnaire', 'form'];
            const reviewStatus = 'submitted';
            const reviewCount = episode.review_count || 0;
            counts.push(
                <Box key={`${episode.id}-review`} sx={{ height: 24, display: 'flex', alignItems: 'center' }}>
                    {reviewCount > 0 && (
                        <Link component={RouterLink} to={`/patients/${patient.id}`}>
                            <Chip
                                label={reviewCount}
                                size="small"
                                color="secondary"
                                sx={{ cursor: 'pointer' }}
                            />
                        </Link>
                    )}
                </Box>
            );
        });

        return { journeyChips: chips, reviewCounts: counts };
    }, [episodes, assignments, isLoadingDetails, patient, onScheduleSurgery]);

    return (
        <TableRow hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
            <TableCell component="th" scope="row">
                <Link component={RouterLink} to={`/patients/${patient.id}`} underline="hover" sx={{ fontWeight: 500 }}>
                    {`${patient.first_name || ''} ${patient.last_name || ''}`}
                </Link>
                <Typography variant="caption" color="text.secondary" component="div">{patient.email}</Typography>
            </TableCell>

            <TableCell>
                <Stack direction="column" spacing={1} alignItems="flex-start">
                    {journeyChips}
                </Stack>
            </TableCell>

            <TableCell align="center">
                <Stack direction="column" spacing={1} alignItems="center">
                    {reviewCounts}
                </Stack>
            </TableCell>

            {/* Combined Alerts & Actions Cell */}
            <TableCell align="center">
                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                    {/* EHR Link Status */}
                    {patient.patient_profile?.external_emr_id ? (
                        <Tooltip title="Patient is linked to the EHR">
                            <CheckCircleOutlineIcon color="success" />
                        </Tooltip>
                    ) : (
                        <Tooltip title="Patient is not linked to the EHR. Click to link.">
                            <IconButton 
                                component={RouterLink} 
                                to={`/patients/${patient.id}`}
                                color="warning"
                                size="small"
                            >
                                <LinkIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}

                    {/* Delete Action (Visible only to Admins) */}
                    {(currentUserRole === 'tenant_admin' || currentUserRole === 'platform_admin') && (
                        <Tooltip title="Permanently Purge Patient">
                            <IconButton 
                                size="small" 
                                color="error" 
                                onClick={(e) => { 
                                    e.preventDefault(); 
                                    e.stopPropagation(); 
                                    onDelete(); 
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            </TableCell>
        </TableRow>
    );
};

const filter = createFilterOptions();

function Dashboard() {
    const { user, selectedTenant, isLoading: authIsLoading } = useAuth();
    const queryClient = useQueryClient();
    const tenantId = selectedTenant?.tenant_id;
    const userRole = selectedTenant?.role;

    const [mainSearchQuery, setMainSearchQuery] = useState('');
    const [isInviteStaffModalOpen, setIsInviteStaffModalOpen] = useState(false);
    const [isBulkInviteModalOpen, setIsBulkInviteModalOpen] = useState(false);
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
    const [schedulerState, setSchedulerState] = useState({ open: false, patient: null, episodes: [] });
    const [wizardState, setWizardState] = useState({ open: false, initialPatientContext: null, initialAction: null });
    const [deleteTarget, setDeleteTarget] = useState({ open: false, patient: null });
    const [confirmText, setConfirmText] = useState('');
    const [isQrGeneratorModalOpen, setIsQrGeneratorModalOpen] = useState(false);
    const debouncedMainSearch = useDebounce(mainSearchQuery, 400);

    const { data: dashboardStats, isLoading: loadingDashboardStats } = useQuery({ queryKey: ['dashboardStats', tenantId], queryFn: fetchDashboardStats, enabled: !!tenantId });
    const { data: patientData = [], isLoading: isLoadingPatients, isFetching: isSearching } = useQuery({
        queryKey: ['accessiblePatientsAndSearch', tenantId, debouncedMainSearch],
        queryFn: () => searchPatientsAPI(debouncedMainSearch),
        enabled: !!tenantId,
        placeholderData: [],
    });

    // ADDED: Fetch all episodes and assignments for the entire tenant
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);

    // Fetch Paginated Overview
    const { data: overviewData, isLoading: isLoadingOverview } = useQuery({
        queryKey: ['clinicianOverview', tenantId, page, debouncedMainSearch],
        queryFn: () => authFetch(`/dashboard/clinician-overview?page=${page}&page_size=${pageSize}${debouncedMainSearch ? `&search=${debouncedMainSearch}` : ''}`),
        enabled: !!tenantId,
        keepPreviousData: true, // Smooth transitions
    });

    const patientList = overviewData?.data || [];
    const totalPages = overviewData ? Math.ceil(overviewData.total / pageSize) : 0;

    const handleActionMenuClick = (event) => setActionMenuAnchorEl(event.currentTarget);
    const handleActionMenuClose = () => setActionMenuAnchorEl(null);
    const handleOpenScheduler = (patient, episodes) => setSchedulerState({ open: true, patient, episodes });

    const handleDeletePatient = async () => {
        try {
            // authFetch handles the DELETE and returns undefined for 204
            await authFetch(`/api/patients/${deleteTarget.patient.id}`, { 
                method: 'DELETE' 
            });

            // If we reach this line, it means authFetch did NOT throw an error
            // 1. IMMEDIATELY clear state to close the modal
            setDeleteTarget({ open: false, patient: null });
            setConfirmText('');
            
            // 2. Silently invalidate queries to refresh the background list
            queryClient.invalidateQueries({ queryKey: ['accessiblePatientsAndSearch'] });
            queryClient.invalidateQueries({ queryKey: ['clinicianOverview'] });
            queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });

        } catch (err) { 
            // 3. Handle actual server errors (authFetch throws Error objects with .data)
            console.error("Delete failed", err);
            
            // Use the error message generated by authFetch or a fallback
            const errorMessage = err.message || "The database restricted this deletion.";
            alert(`Server Error: ${errorMessage}`);
        }
    };

    const handleStartWizard = (patientContext, action = null) => {
        setWizardState({ open: true, initialPatientContext: patientContext, initialAction: action });
    };

    const handleMainSearchSelect = (event, newValue) => {
        if (typeof newValue === 'string' || !newValue) return;

        if (newValue.isNew) {
            handleStartWizard(null, 'invite_new');
        } else {
            handleStartWizard(newValue);
        }
    };

    const sortedPatients = useMemo(() => {
        const patients = Array.isArray(patientData) ? patientData : [];
        return [...patients].sort((a, b) => {
            if (a.status === 'provisional' && b.status !== 'provisional') return -1;
            if (a.status !== 'provisional' && b.status === 'provisional') return 1;
            return `${a.last_name || ''} ${a.first_name || ''}`.localeCompare(`${b.last_name || ''} ${b.first_name || ''}`);
        });
    }, [patientData]);

    if (authIsLoading) return <Container sx={{ textAlign: 'center', pt: 10 }}><CircularProgress size={50} /></Container>;
    if (!selectedTenant) return <Container maxWidth="md" sx={{ mt: 5 }}><Alert severity="info">Please select a tenant to view your dashboard.</Alert></Container>;

    const showAdminActions = userRole === 'tenant_admin' || userRole === 'physician_qhp';

    return (
        <>
            <Box sx={{ flexGrow: 1, width: '100%', py: { xs: 2, md: 4 }, px: { xs: 2, md: 4 }, backgroundColor: (theme) => theme.palette.grey[50] }}>
                <Container maxWidth="xl">
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="flex-end" alignItems="center" mb={3} gap={2}>
                        {/* <Typography variant="h4" color="text.secondary">Welcome back, {user?.first_name || 'Clinician'}.</Typography> */}
                        {showAdminActions && (
                            <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                                variant="outlined" // Or "contained" if you prefer
                                startIcon={<QrCode2Icon />}
                                onClick={() => setIsQrGeneratorModalOpen(true)} // Opens the QR modal
                            >
                                Program QR Codes
                            </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<PersonAddAlt1Icon />}
                                    onClick={() => handleStartWizard(null, 'invite_new')}
                                >
                                    Add New Patient
                                </Button>
                                <Box>
                                    <Button id="action-menu-button" variant="outlined" endIcon={<KeyboardArrowDownIcon />} onClick={handleActionMenuClick}>More Actions</Button>
                                        <Menu id="action-menu" anchorEl={actionMenuAnchorEl} open={Boolean(actionMenuAnchorEl)} onClose={handleActionMenuClose}>
                                            <MenuItem onClick={() => { setIsInviteStaffModalOpen(true); handleActionMenuClose(); }}><ListItemIcon><GroupAddIcon /></ListItemIcon><ListItemText>Invite Staff</ListItemText></MenuItem>
                                            <MenuItem onClick={() => { setIsBulkInviteModalOpen(true); handleActionMenuClose(); }}><ListItemIcon><CloudUploadIcon /></ListItemIcon><ListItemText>Bulk Invite Patients</ListItemText></MenuItem>
                                        </Menu>
                                </Box>
                            </Stack>
                        )}
                    </Stack>

                    <Grid container spacing={3} sx={{ mb: 4 }}><Grid item xs={12}><KpiPanel><KpiItem label="Active Patients" value={dashboardStats?.active_patient_count} isLoading={loadingDashboardStats} /><KpiItem label="Active Episodes" value={dashboardStats?.episodes_active_count} isLoading={loadingDashboardStats} /><KpiItem label="Responses to Review" value={dashboardStats?.surveys_awaiting_review_count} isLoading={loadingDashboardStats} /></KpiPanel></Grid></Grid>

                    <Paper variant="outlined">
                        <Box sx={{ p: 2 }}>
                            <Autocomplete
                                fullWidth
                                value={null}
                                onChange={handleMainSearchSelect}
                                onInputChange={(e, newInputValue) => setMainSearchQuery(newInputValue)}
                                filterOptions={(options, params) => {
                                    const filtered = filter(options, params);
                                    const { inputValue } = params;
                                    if (inputValue.length > 2 && !isSearching) {
                                        const isExisting = options.some(opt => (opt.first_name + ' ' + opt.last_name).toLowerCase().includes(inputValue.toLowerCase()));
                                        if (!isExisting) {
                                            filtered.push({
                                                inputValue,
                                                label: `Invite "${inputValue}" as a new patient`,
                                                isNew: true,
                                            });
                                        }
                                    }
                                    return filtered;
                                }}
                                options={patientData}
                                getOptionLabel={(option) => option.isNew ? option.label : `${option.last_name || ''}, ${option.first_name || ''} (${option.email || 'N/A'})`}
                                renderOption={(props, option) => <li {...props}>{option.label || `${option.last_name}, ${option.first_name}`}</li>}
                                loading={isSearching}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search patient to begin a new episode"
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                                            endAdornment: <>{isSearching ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>,
                                        }}
                                    />
                                )}
                            />
                        </Box>
                        <TableContainer>
                            <Table sx={{ minWidth: 650 }}>
                                <TableHead sx={{ bgcolor: 'action.hover' }}>
                                    <TableRow>
                                        <TableCell>Patient</TableCell>
                                        <TableCell sx={{ minWidth: 320 }}>Episodes Status</TableCell>
                                        <TableCell align="center">Responses to Review</TableCell>
                                        <TableCell align="center">Alerts</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {/* UPDATED: Loading state now checks all three queries */}
                                    {isLoadingOverview ? (
                                        <TableRow><TableCell colSpan={4} align="center" sx={{ p: 4 }}><CircularProgress /></TableCell></TableRow>
                                    ) : patientList.length === 0 ? (
                                        <TableRow><TableCell colSpan={4} align="center" sx={{ p: 4 }}>No patients found.</TableCell></TableRow>
                                    ) : (
                                        patientList.map((patient) => (
                                            <PatientRow
                                                key={patient.id}
                                                patient={patient}
                                                onScheduleSurgery={handleOpenScheduler}
                                                episodes={patient.episodes}
                                                assignments={[]} 
                                                isLoadingDetails={false}
                                                onDelete={() => setDeleteTarget({ open: true, patient: patient })}
                                                currentUserRole={userRole}
                                            />
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {/* Add after TableContainer */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <Button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                            <Typography sx={{ mx: 2, alignSelf: 'center' }}>Page {page} of {totalPages || 1}</Typography>
                            <Button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
                        </Box>
                    </Paper>
                    <Dialog open={deleteTarget.open} onClose={() => setDeleteTarget({ open: false, patient: null })}>
                        <DialogTitle>Purge Patient Record?</DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" color="error" gutterBottom>
                                Type "DELETE" to permanently remove <strong>{deleteTarget.patient?.first_name} {deleteTarget.patient?.last_name}</strong>.
                            </Typography>
                            <TextField fullWidth label="Type DELETE to confirm" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} margin="normal" />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDeleteTarget({ open: false, patient: null })}>Cancel</Button>
                            <Button onClick={handleDeletePatient} color="error" variant="contained" disabled={confirmText !== 'DELETE'}>Confirm Purge</Button>
                        </DialogActions>
                    </Dialog>
                </Container>
            </Box>

            {wizardState.open && tenantId && (
                <UnifiedCareWizard
                    open={wizardState.open}
                    onClose={(submitted) => {
                        setWizardState({ open: false, initialPatientContext: null, initialAction: null });
                        if (submitted) {
                            queryClient.invalidateQueries({ queryKey: ['accessiblePatientsAndSearch'] });
                            // Invalidate the new queries as well
                            queryClient.invalidateQueries({ queryKey: ['allTenantEpisodes'] });
                            queryClient.invalidateQueries({ queryKey: ['allTenantAssignments'] });
                        }
                    }}
                    tenantId={tenantId}
                    initialPatientContext={wizardState.initialPatientContext}
                    initialAction={wizardState.initialAction}
                />
            )}
            <InviteStaffModal open={isInviteStaffModalOpen} onClose={() => setIsInviteStaffModalOpen(false)} />
            {isBulkInviteModalOpen && <BulkInviteModal open={isBulkInviteModalOpen} onClose={() => setIsBulkInviteModalOpen(false)} />}
            {schedulerState.open && <ScheduleSurgeryModal open={schedulerState.open} onClose={() => setSchedulerState({ open: false, patient: null, episodes: [] })} episodes={schedulerState.episodes} patientUserId={schedulerState.patient?.id} tenantId={tenantId} />}
            {isQrGeneratorModalOpen && (
                <ProgramInviteGeneratorModal
                    open={isQrGeneratorModalOpen}
                    onClose={() => setIsQrGeneratorModalOpen(false)}
                />
            )}
        </>
    );
}

export default Dashboard;